<div class="console-loading" *ngIf="loading">
  <div class="spinner"></div>
  <p>Loading your Sage console&hellip;</p>
</div>

<!-- A wrapper, not classes on .console-page: Bootstrap's d-md-block is !important and would override its flex layout. -->
<div class="d-none d-md-block">
<div class="console-page" *ngIf="!loading">
  <div class="page-header">
    <div>
      <h1 class="page-title">Sage Integration</h1>
      <p class="page-subtitle">Connection status, sync health, errors and conflicts</p>
    </div>
    <div class="header-right">
      <button type="button" class="help-btn" data-tour="help" *ngIf="!loadError" [class.pulse]="helpPulse" (click)="openTour()">
        <i class="bi bi-question-circle" aria-hidden="true"></i>Help
      </button>
      <div class="user-block" *ngIf="fullName">
        <span class="user-label">Current User:</span>
        <div class="user-name">{{ fullName }}</div>
        <div class="user-tenant">{{ tenantName }}</div>
      </div>
      <div class="console-menu">
        <button
          id="consoleMenuBtn"
          class="btn-icon-only"
          aria-haspopup="true"
          [attr.aria-expanded]="menuOpen"
          aria-label="Sage integration options"
          (click)="toggleMenu()"
        >
          <i class="bi bi-three-dots-vertical"></i>
        </button>
        <div id="consoleMenu" class="console-menu-dropdown" *ngIf="menuOpen">
          <button class="menu-item menu-item-danger" [disabled]="disconnecting" (click)="disconnectSage()">
            <i class="bi bi-plug"></i> Disconnect Sage
          </button>
        </div>
      </div>
    </div>
  </div>

  <div class="alert-banner" *ngIf="loadError">
    <i class="bi bi-exclamation-circle"></i>
    We couldn't load your Sage connection. Please refresh the page.
  </div>

  <div class="paused-banner" *ngIf="!loadError && !syncEnabled">
    <i class="bi bi-pause-circle"></i>
    <span>Sync is paused for this organisation. The Sage connection is kept alive; no data is sent to or read from Sage.</span>
  </div>

  <ng-container *ngIf="!loadError">
    <!-- Connection card -->
    <div class="conn-card" [class.has-stepper]="showSyncStepper">
      <div class="conn-head">
        <div class="conn-icon"><i class="bi bi-plug"></i></div>
        <div class="conn-body">
          <div class="conn-title-row">
            <span class="conn-title">Sage Business Cloud Accounting</span>
            <span class="chip connected"><span></span>Connected</span>
            <span class="chip paused" *ngIf="!syncEnabled"><span></span>Sync paused</span>
          </div>
          <!-- One element that stays mounted, so screen readers announce each step change and completion. -->
          <p class="conn-sub" aria-live="polite">{{ connSubtitle }}</p>
          <div class="conn-token-line" *ngIf="lastSyncedAt">
            Last synced {{ lastSyncedAt | date: 'short' }}
          </div>
        </div>
        <div class="conn-actions">
          <button class="btn btn-primary sync-all-btn" [disabled]="!syncEnabled || syncState.kind !== 'idle'" (click)="syncAllNow()">
            <ng-container *ngIf="syncAllShowsSyncing; else syncAllIdle">
              <span class="sync-spinner on-dark" aria-hidden="true"></span>
              <i class="bi bi-hourglass-split sync-static-icon" aria-hidden="true"></i>Syncing…
            </ng-container>
            <ng-template #syncAllIdle><i class="bi bi-play-fill"></i>Sync all now</ng-template>
          </button>
        </div>
      </div>

      <ol class="sync-stepper" *ngIf="showSyncStepper" aria-label="Sync progress">
        <li
          class="sync-step"
          *ngFor="let step of syncProgress.steps; let i = index; let last = last; trackBy: trackStep"
          [ngClass]="'is-' + step.status"
        >
          <span class="step-dot" aria-hidden="true" [ngSwitch]="step.status">
            <i *ngSwitchCase="'done'" class="bi bi-check-lg"></i>
            <ng-container *ngSwitchCase="'syncing'">
              <span class="sync-spinner on-dark"></span>
              <i class="bi bi-hourglass-split sync-static-icon"></i>
            </ng-container>
            <ng-container *ngSwitchCase="'failed'">!</ng-container>
            <i *ngSwitchCase="'notRun'" class="bi bi-dash-lg"></i>
            <ng-container *ngSwitchDefault>{{ i + 1 }}</ng-container>
          </span>
          <span class="step-line" *ngIf="!last" aria-hidden="true"></span>
          <span class="step-name">{{ step.label }}</span>
          <span class="step-caption" [ngSwitch]="step.status">
            <ng-container *ngSwitchCase="'done'">Done</ng-container>
            <ng-container *ngSwitchCase="'syncing'">Syncing…</ng-container>
            <ng-container *ngSwitchCase="'failed'">
              Failed – <button type="button" class="link-btn" (click)="selectTab('errors')">view</button>
            </ng-container>
            <ng-container *ngSwitchCase="'notRun'">Not run</ng-container>
            <ng-container *ngSwitchDefault>Queued</ng-container>
          </span>
        </li>
      </ol>
    </div>

    <!-- Tabs -->
    <div class="tabs" role="tablist">
      <button class="tab-btn" [class.active]="activeTab === 'sync'" (click)="selectTab('sync')">
        <i class="bi bi-arrow-left-right"></i>Sync dashboard
      </button>
      <button class="tab-btn" data-tour="tab-matches" [class.active]="activeTab === 'matches'" (click)="selectTab('matches')">
        <i class="bi bi-link-45deg"></i>Fix matches
      </button>
      <button class="tab-btn" data-tour="tab-runs" [class.active]="activeTab === 'runs'" (click)="selectTab('runs')">
        <i class="bi bi-clock-history"></i>Recent sync runs
      </button>
      <!-- Review queue, Errors and Stock corrections have no tab of their own: the Sync dashboard's
           work columns are the way in, and each panel carries a back button. Conflicts keeps its
           tab because nothing on the dashboard links to it. -->
      <button class="tab-btn" data-tour="tab-conflicts" [class.active]="activeTab === 'conflicts'" (click)="selectTab('conflicts')">
        <i class="bi bi-shuffle"></i>Conflicts
        <span class="count-pill warn" *ngIf="conflictBadgeCount">{{ conflictBadgeCount }}</span>
      </button>
      <button class="tab-btn" data-tour="tab-groups" [class.active]="activeTab === 'groups'" (click)="selectTab('groups')">
        <i class="bi bi-tags"></i>Sales &amp; purchase groups
      </button>
    </div>

    <!-- Sync dashboard -->
    <section class="tab-panel" *ngIf="activeTab === 'sync'">
      <div class="entity-carousel">
        <button
          type="button"
          class="carousel-nav prev"
          *ngIf="canScrollEntitiesBack"
          (click)="scrollEntities(-1)"
          aria-label="Previous record types"
        >
          <i class="bi bi-chevron-left"></i>
        </button>
        <div class="entity-track" #entityTrack (scroll)="updateEntityScrollState()">
          <div
            class="entity-card"
            *ngFor="let e of entityStatuses; trackBy: trackEntity"
            [attr.data-tour]="entitySlug(e.entityType)"
            [style.--card-accent]="entityAccent(e.entityType)"
            [class.is-syncing]="cardSyncState(e.entityType) === 'syncing'"
            [class.is-sync-done]="cardSyncState(e.entityType) === 'done'"
            [class.is-sync-failed]="cardSyncState(e.entityType) === 'failed'"
          >
            <div class="sync-shimmer" *ngIf="cardSyncState(e.entityType) === 'syncing'" aria-hidden="true"><span></span></div>
            <div class="entity-head">
              <div class="entity-icon" [style.background]="entityAccentSoft(e.entityType)" [style.color]="entityAccent(e.entityType)">
                <i class="bi" [ngClass]="entityIcon(e.entityType)"></i>
              </div>
              <div class="entity-name-block">
                <div class="entity-name">{{ entityLabel(e.entityType) }}</div>
                <div class="entity-dir">
                  {{ e.outbound ? syncDirectionLabel(e.outbound.direction) : (e.inbound ? syncDirectionLabel(e.inbound.direction) : 'Not yet synced') }}
                </div>
              </div>
              <ng-container [ngSwitch]="cardSyncState(e.entityType)">
                <span class="result-chip" *ngSwitchCase="'done'"><i class="bi bi-check-lg"></i>Done</span>
                <!-- Counts only errors the user can clear; auto-retrying ones stay in "not synced", which is
                     why a failed sync can still have nothing to count. -->
                <span
                  class="health-pill"
                  *ngSwitchDefault
                  [class.failed]="actionableCountFor(e.entityType) > 0 || cardSyncState(e.entityType) === 'failed'"
                >
                  {{ actionableCountFor(e.entityType) > 0
                      ? actionableCountFor(e.entityType) + ' to fix'
                      : cardSyncState(e.entityType) === 'failed' ? 'Sync failed' : 'Healthy' }}
                </span>
              </ng-container>
            </div>
            <div class="metrics">
              <div class="metric">
                <b>{{ e.outbound?.recordsSucceeded || 0 }}</b>
                <span>in sync</span>
              </div>
              <div class="metric pending">
                <b>{{ e.outbound?.recordsSkipped || 0 }}</b>
                <span>skipped</span>
              </div>
              <div class="metric errors">
                <b [class.has-err]="(e.outbound?.recordsFailed || 0) > 0">{{ e.outbound?.recordsFailed || 0 }}</b>
                <span>errors</span>
              </div>
            </div>
            <div class="entity-foot" [ngSwitch]="cardSyncState(e.entityType)">
              <span class="sync-pill" *ngSwitchCase="'syncing'">
                <span class="sync-spinner" aria-hidden="true"></span>
                <i class="bi bi-hourglass-split sync-static-icon" aria-hidden="true"></i>Syncing…
              </span>
              <span class="foot-result" *ngSwitchCase="'done'">Synced just now</span>
              <ng-container *ngSwitchCase="'failed'">
                <button type="button" class="link-btn foot-result failed" (click)="selectTab('errors')">View errors</button>
                <button
                  class="btn btn-primary sync-btn"
                  [disabled]="!syncEnabled || syncState.kind !== 'idle'"
                  (click)="syncEntity(e.entityType)"
                >
                  <i class="bi bi-play-fill"></i>Sync now
                </button>
              </ng-container>
              <ng-container *ngSwitchDefault>
                <i class="bi bi-clock-history"></i>
                <span *ngIf="e.outbound?.completedAt">Synced {{ e.outbound?.completedAt | date: 'short' }}</span>
                <span *ngIf="!e.outbound?.completedAt">Not yet synced</span>
                <button
                  class="btn btn-primary sync-btn"
                  [disabled]="!syncEnabled || syncState.kind !== 'idle'"
                  (click)="syncEntity(e.entityType)"
                >
                  <i class="bi bi-play-fill"></i>Sync now
                </button>
              </ng-container>
            </div>
          </div>
        </div>
        <button
          type="button"
          class="carousel-nav next"
          *ngIf="canScrollEntitiesForward"
          (click)="scrollEntities(1)"
          aria-label="Next record types"
        >
          <i class="bi bi-chevron-right"></i>
        </button>
      </div>

      <div class="work-columns">
        <app-sage-review-column
          [groups]="reviewGroups"
          [totalCount]="reviewTotalCount"
          [loading]="loadingReviewGroups"
          (openQueue)="selectTab('review')"
          (openGroup)="openReviewGroup($event)"
        ></app-sage-review-column>

        <app-sage-errors-column
          [groups]="errorGroups"
          [retryingCount]="retryingErrorCount"
          [loading]="loadingErrorGroups"
          (openIssues)="selectTab('errors')"
          (fixGroup)="fixErrorGroup($event)"
        ></app-sage-errors-column>

        <app-sage-stock-column
          [rows]="dashStockRows"
          [totalCount]="dashStockTotal"
          [reasons]="stockReasons"
          [syncEnabled]="syncEnabled"
          [loading]="loadingDashStock"
          (changed)="onDashStockChanged()"
          (viewAll)="selectTab('stock')"
          (syncDisabled)="syncEnabled = false"
        ></app-sage-stock-column>
      </div>
    </section>

    <!-- Recent sync runs -->
    <section class="tab-panel table-card" *ngIf="activeTab === 'runs'">
      <div class="table-card-head">
        <h3>Recent sync runs</h3>
        <span class="cap">Newest first</span>
      </div>
      <div class="tbl-scroll">
        <table class="tbl">
          <thead>
            <tr><th>Started</th><th>What synced</th><th>Direction</th><th>How it ran</th><th>Records</th><th>Result</th></tr>
          </thead>
          <tbody>
            <tr *ngFor="let log of recentLogs">
              <td class="mono">{{ log.startedAt | date: 'short' }}</td>
              <td>{{ syncRunEntityLabel(log.entityType) }}</td>
              <td>
                <span
                  class="dir-pill"
                  [class.outbound]="syncDirectionName(log.direction) === 'Outbound'"
                  [class.inbound]="syncDirectionName(log.direction) === 'Inbound'"
                >
                  <i class="bi" [ngClass]="syncDirectionIcon(log.direction)"></i>
                  {{ syncDirectionLabel(log.direction) }}
                </span>
              </td>
              <td>{{ syncTriggerLabel(log.trigger) }}</td>
              <td class="mono">{{ log.recordsProcessed }} processed &middot; {{ log.recordsSucceeded }} succeeded</td>
              <td>
                <span class="result-pill success" *ngIf="syncStatusName(log.status) === 'Succeeded'">Success</span>
                <span class="result-pill partial" *ngIf="syncStatusName(log.status) === 'PartialFailure' || syncStatusName(log.status) === 'Failed'">
                  Completed with errors
                </span>
                <span class="result-pill held" *ngIf="syncStatusName(log.status) === 'Running'">Running</span>
              </td>
            </tr>
            <tr *ngIf="loadingRuns && !recentLogs.length"><td colspan="6" class="empty-cell">Loading&hellip;</td></tr>
            <tr *ngIf="!loadingRuns && !recentLogs.length"><td colspan="6" class="empty-cell">No sync runs yet.</td></tr>
          </tbody>
        </table>
      </div>

      <div class="err-pager" *ngIf="runsTotalPages > 1">
        <button type="button" [disabled]="runsPage <= 1 || loadingRuns" aria-label="Previous page" (click)="goToRunsPage(runsPage - 1)">
          <i class="bi bi-chevron-left"></i>
        </button>
        <span class="pager-pages">
          <ng-container *ngFor="let p of runsPageNumbers">
            <span class="pager-gap" *ngIf="p === null" aria-hidden="true">&hellip;</span>
            <button
              type="button"
              class="pager-num"
              *ngIf="p !== null"
              [class.active]="p === runsPage"
              [attr.aria-current]="p === runsPage ? 'page' : null"
              [attr.aria-label]="'Page ' + p"
              [disabled]="loadingRuns"
              (click)="goToRunsPage(p)"
            >
              {{ p }}
            </button>
          </ng-container>
        </span>
        <button type="button" [disabled]="runsPage >= runsTotalPages || loadingRuns" aria-label="Next page" (click)="goToRunsPage(runsPage + 1)">
          <i class="bi bi-chevron-right"></i>
        </button>
      </div>
    </section>

    <!-- Fix matches -->
    <section class="tab-panel fm-tab" *ngIf="activeTab === 'matches'">
      <div class="mk-toggle" role="group" aria-label="Records">
        <button
          type="button"
          *ngFor="let section of fixSections"
          [class.on]="fixSection === section"
          [attr.aria-pressed]="fixSection === section"
          (click)="setFixSection(section)"
        >
          {{ fixSectionLabel(section, fixSectionCounts[section]) }}
        </button>
      </div>

      <ng-container *ngIf="fixSection !== 'FinishedGood'">
        <div class="mk-tools">
          <label class="mk-search">
            <i class="bi bi-search" aria-hidden="true"></i>
            <input
              type="text"
              autocomplete="off"
              [placeholder]="fixType === 'Item' ? 'Search by name or code' : 'Search by supplier name'"
              [attr.aria-label]="fixType === 'Item' ? 'Search by name or code' : 'Search by supplier name'"
              [(ngModel)]="linksSearch"
            />
          </label>
          <button
            type="button"
            class="mk-chip"
            *ngFor="let chip of fixChips"
            [class.on]="linksChip === chip.key"
            [attr.aria-pressed]="linksChip === chip.key"
            (click)="linksChip = chip.key"
          >
            {{ fixChipLabel(chip) }}
          </button>
        </div>

        <table class="mk-table">
          <thead>
            <tr>
              <th>Aivor {{ fixNoun }}</th>
              <th>Linked Sage {{ fixNoun }}</th>
              <th *ngIf="fixType === 'Item'">Type</th>
              <th>Last synced</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let row of filteredLinkRows; trackBy: trackLink" [class.fm-fixed-row]="isLinkFixed(row)">
              <td>
                <b>{{ row.name }}</b>
                <div class="mk-code" *ngIf="row.code">{{ row.code }}</div>
              </td>
              <td>
                <ng-container *ngIf="row.originalSageName; else addedByAivor">{{ row.originalSageName }}</ng-container>
                <ng-template #addedByAivor><span class="mk-muted">Added to Sage by Aivor</span></ng-template>
                <span class="mk-pill warn" *ngIf="linkAttentionFor(row) as issue">{{ issue }}</span>
                <div class="mk-code" *ngIf="row.originalSageCode">{{ row.originalSageCode }}</div>
              </td>
              <td *ngIf="fixType === 'Item'">
                <span class="mk-pill" [ngClass]="linkTypePill(row)">{{ linkTypeLabel(row) }}</span>
              </td>
              <td>
                <span class="mk-pill ok" *ngIf="isLinkFixed(row); else syncedAt">Fixed just now</span>
                <ng-template #syncedAt>
                  <span class="mk-muted fm-when">{{ row.lastSyncedAt ? relativeDayLabel(row.lastSyncedAt) : 'Not synced yet' }}</span>
                </ng-template>
              </td>
              <td class="fm-action-cell">
                <button type="button" class="mk-btn" *ngIf="canChangeLink(row)" [disabled]="!syncEnabled" (click)="openLinkChange(row)">Change</button>
              </td>
            </tr>
            <tr *ngIf="loadingLinks && !linkRows.length">
              <td [attr.colspan]="fixType === 'Item' ? 5 : 4" class="empty-cell">Loading&hellip;</td>
            </tr>
            <tr *ngIf="linksLoadError">
              <td [attr.colspan]="fixType === 'Item' ? 5 : 4" class="empty-cell">
                Could not load the links. <button type="button" class="link-btn" (click)="loadFixMatches()">Try again</button>
              </td>
            </tr>
            <tr *ngIf="!loadingLinks && !linksLoadError && !filteredLinkRows.length">
              <td [attr.colspan]="fixType === 'Item' ? 5 : 4" class="empty-cell">{{ emptyLinksText }}</td>
            </tr>
          </tbody>
        </table>
      </ng-container>

      <div class="table-card" *ngIf="fixSection === 'FinishedGood'">
        <div class="section-head">
          <div class="icon-badge teal"><i class="bi bi-box-seam"></i></div>
          <div class="section-head-text">
            <h3>How finished goods appear in Sage</h3>
            <p>
              {{ targets.length }} finished good(s)
              <span *ngIf="unsetTargetCount > 0" class="blocked-badge" title="These haven't been saved as Bundle or Standalone yet, so they won't sync until you click Save changes">
                <i class="bi bi-exclamation-triangle-fill"></i> {{ unsetTargetCount }} not saved as Bundle or Standalone
              </span>
            </p>
          </div>
          <div class="search-input">
            <i class="bi bi-search"></i>
            <input type="text" placeholder="Search finished goods" [(ngModel)]="targetsSearch" autocomplete="off" />
          </div>
        </div>
        <div class="tbl-scroll">
          <table class="tbl">
            <thead>
              <tr><th>Finished good</th><th>Item code</th><th>Made from</th><th>Last synced</th><th>Sends to Sage as</th></tr>
            </thead>
            <tbody>
              <tr *ngFor="let t of filteredTargets">
                <td>
                  <div class="cell-primary">{{ t.itemName }}</div>
                  <div class="cell-sub">{{ t.categoryName }}</div>
                </td>
                <td class="code muted">{{ t.itemCode }}</td>
                <td>
                  {{ t.componentCount }} item(s)
                  <span
                    *ngIf="isTargetBlocked(t)"
                    class="blocked-badge"
                    [title]="targetBlockedReason(t)"
                  >
                    <i class="bi bi-exclamation-triangle-fill"></i> Can't sync yet
                  </span>
                </td>
                <td class="prov-cell">
                  <button
                    class="prov-trigger"
                    [class.warn]="provenanceStateFor(t) === 'push-pending'"
                    [class.muted]="provenanceStateFor(t) === 'never-synced'"
                    (click)="toggleProvenance(t)"
                    aria-haspopup="true"
                  >
                    <i class="bi bi-clock-history"></i>
                    <ng-container [ngSwitch]="provenanceStateFor(t)">
                      <span *ngSwitchCase="'in-sync'">Synced</span>
                      <span *ngSwitchCase="'push-pending'">Not sent yet</span>
                      <span *ngSwitchCase="'never-synced'">Never synced</span>
                      <span *ngSwitchDefault>Last synced</span>
                    </ng-container>
                  </button>
                  <div class="prov-pop" *ngIf="openProvenanceItemId === t.itemId">
                    <div class="prov-pop-title">
                      Sync history
                      <span class="prov-pop-sub">Item &middot; {{ t.itemCode }}</span>
                    </div>

                    <div *ngIf="provenanceFor(t) === 'loading'" class="prov-pop-loading">Loading…</div>
                    <div *ngIf="provenanceFor(t) === 'error'" class="prov-pop-error">
                      Could not load sync detail for this item.
                    </div>

                    <ng-container *ngIf="provenanceFor(t) as p">
                      <ng-container *ngIf="p !== 'loading' && p !== 'error'">
                        <dl class="prov-list">
                          <dt>Last synced with Sage</dt>
                          <dd [class.prov-stale]="!p.sageLastSyncedAt">
                            {{ p.sageLastSyncedAt ? (p.sageLastSyncedAt | date: 'short') : '- never synced' }}
                          </dd>
                          <dt>Last sent to Sage</dt>
                          <dd [class.prov-stale]="!p.sageLastPushedAt">
                            {{ p.sageLastPushedAt ? (p.sageLastPushedAt | date: 'short') : '- never sent' }}
                          </dd>
                          <dt>Reference (for support)</dt>
                          <dd class="mono">{{ p.sageLastPayloadHash || '-' }}</dd>
                        </dl>
                        <div class="prov-pop-note" [class.warn]="provenanceStateFor(t) === 'push-pending'">
                          <i class="bi bi-info-circle"></i>
                          <span *ngIf="provenanceStateFor(t) === 'in-sync'">Up to date - nothing waiting to send.</span>
                          <span *ngIf="provenanceStateFor(t) === 'push-pending'">Aivor's latest changes haven't reached Sage yet - check Sync errors for the reason.</span>
                          <span *ngIf="provenanceStateFor(t) === 'never-synced'">This item hasn't been synced yet.</span>
                        </div>
                      </ng-container>
                    </ng-container>
                  </div>
                </td>
                <td>
                  <div class="toggle-row">
                    <span class="toggle-label" [class.bundle]="t.target === 'Bundle'" [class.standalone]="t.target === 'Standalone'">
                      {{ t.target }}
                    </span>
                    <button class="switch" [class.on]="t.target === 'Bundle'" (click)="toggleTarget(t)">
                      <span class="knob"></span>
                    </button>
                    <span *ngIf="!t.hasSavedTarget" class="blocked-badge" title="Not saved yet — click Save changes below or it will not sync">Unsaved</span>
                  </div>
                </td>
              </tr>
              <tr *ngIf="!filteredTargets.length"><td colspan="5" class="empty-cell">No finished goods match your search.</td></tr>
            </tbody>
          </table>
        </div>
        <div class="targets-footer">
          <i class="bi bi-info-circle"></i>
          <span>Bundle: Sage gets a bundle that lists the items it's made from. Standalone: Sage gets one item, with the cost and price of those items added together.</span>
          <span class="dirty-count" [class.dirty]="dirtyTargetCount > 0" [class.clean]="dirtyTargetCount === 0">
            {{ dirtyTargetCount > 0 ? dirtyTargetCount + ' unsaved change(s)' : 'All changes saved' }}
          </span>
          <button class="btn btn-primary" [disabled]="!dirtyTargetCount || savingTargets" (click)="saveTargets()">
            {{ savingTargets ? 'Saving…' : 'Save changes' }}
          </button>
        </div>
      </div>
    </section>

    <!-- Errors -->
    <section class="tab-panel table-card" *ngIf="activeTab === 'errors'">
      <div class="section-head">
        <button class="back-btn" title="Back to dashboard" aria-label="Back to dashboard" (click)="backToDashboard()">
          <i class="bi bi-arrow-left"></i>
        </button>
        <div class="icon-badge err"><i class="bi bi-exclamation-triangle-fill"></i></div>
        <div class="section-head-text">
          <h3>Sync errors</h3>
          <ng-container *ngIf="!showResolvedErrors">
            <p *ngIf="errorTotalCount">{{ errorFilteredCount }} of {{ errorTotalCount }} outstanding error(s)</p>
            <p *ngIf="!errorTotalCount">No outstanding errors — every blocked or rejected record has been resolved.</p>
          </ng-container>
          <ng-container *ngIf="showResolvedErrors">
            <p *ngIf="errorTotalCount">{{ errorFilteredCount }} of {{ errorTotalCount }} error(s), including resolved ones</p>
            <p *ngIf="!errorTotalCount">No sync errors have been recorded.</p>
          </ng-container>
        </div>
      </div>

      <div class="issue-filter-row">
        <span class="reason-label" id="errorReasonLabel" *ngIf="reasonChipsVisible">Reason</span>
        <div class="reason-chips" role="group" aria-labelledby="errorReasonLabel" *ngIf="reasonChipsVisible">
          <button
            type="button"
            class="err-chip"
            *ngFor="let c of errorCategoryCounts"
            [class.active]="selectedErrorCategories.includes(c.category)"
            [attr.aria-pressed]="selectedErrorCategories.includes(c.category)"
            [disabled]="loadingErrors"
            (click)="toggleErrorCategory(c.category)"
          >
            {{ issueReasonLabel(c.category) }} <span class="err-chip-count">{{ c.count }}</span>
          </button>
        </div>
        <button
          type="button"
          class="grp-toggle"
          [class.on]="showResolvedErrors"
          [attr.aria-pressed]="showResolvedErrors"
          [disabled]="loadingErrors"
          (click)="toggleResolvedErrors()"
        >
          <i class="bi" [class.bi-square]="!showResolvedErrors" [class.bi-check-square-fill]="showResolvedErrors"></i>
          Show resolved
        </button>
        <div class="err-search">
          <input
            type="search"
            placeholder="Search by name, code or message…"
            aria-label="Search errors by name, code or message"
            [ngModel]="errorSearch"
            (ngModelChange)="onErrorSearchChange($event)"
            name="errorSearch"
            autocomplete="off"
          />
          <i class="bi bi-search" aria-hidden="true"></i>
        </div>
      </div>

      <p class="issue-count-live" aria-live="polite">
        <ng-container *ngIf="!loadingErrors">
          {{ errorFilteredCount }} error{{ errorFilteredCount === 1 ? '' : 's' }} shown
        </ng-container>
        <ng-container *ngIf="loadingErrors">Loading errors…</ng-container>
      </p>

      <ng-container *ngTemplateOutlet="issueRowList; context: { $implicit: errorRows }"></ng-container>

      <div class="err-empty" *ngIf="!loadingErrors && !errorRows.length">
        <i class="bi bi-check2-circle"></i>
        <span *ngIf="errorSearch.trim() || selectedErrorCategories.length">No errors match your filters.</span>
        <ng-container *ngIf="!errorSearch.trim() && !selectedErrorCategories.length">
          <span *ngIf="!showResolvedErrors">Nothing outstanding — every record has synced successfully.</span>
          <span *ngIf="showResolvedErrors">No sync errors have been recorded.</span>
        </ng-container>
      </div>

      <div class="err-pager" *ngIf="errorTotalPages > 1">
        <button type="button" [disabled]="errorPage <= 1 || loadingErrors" aria-label="Previous page" (click)="goToErrorPage(errorPage - 1)">
          <i class="bi bi-chevron-left"></i>
        </button>
        <span>Page {{ errorPage }} of {{ errorTotalPages }}</span>
        <button type="button" [disabled]="errorPage >= errorTotalPages || loadingErrors" aria-label="Next page" (click)="goToErrorPage(errorPage + 1)">
          <i class="bi bi-chevron-right"></i>
        </button>
      </div>

      <div class="readonly-note" *ngIf="errorRows.length">
        <i class="bi bi-info-circle"></i>
        <ng-container *ngIf="!showResolvedErrors">An error clears by itself once its record syncs. Fix the cause shown, then sync again.</ng-container>
        <ng-container *ngIf="showResolvedErrors">Errors marked Resolved have cleared because their record has synced since. They are kept as history.</ng-container>
      </div>
    </section>

    <!-- Conflicts -->
    <section class="tab-panel table-card" *ngIf="activeTab === 'conflicts'">
      <div class="section-head">
        <div class="icon-badge warn"><i class="bi bi-shuffle"></i></div>
        <div class="section-head-text">
          <h3>Conflict log</h3>
          <p *ngIf="conflictFilteredCount">
            {{ conflictFilteredCount }} Sage change{{ conflictFilteredCount === 1 ? '' : 's' }} replaced by Aivor.
          </p>
          <p *ngIf="!conflictFilteredCount">No conflicts have been detected.</p>
        </div>
      </div>

      <div class="issue-filter-row">
        <select
          class="conflict-entity-select"
          aria-label="Filter conflicts by record type"
          [(ngModel)]="selectedConflictEntityType"
          (ngModelChange)="onConflictEntityTypeChange()"
          name="conflictEntityType"
        >
          <option value="">All record types</option>
          <option *ngFor="let e of conflictEntityOptions" [value]="e.type">{{ e.label }}</option>
        </select>
        <div class="err-search">
          <input
            type="search"
            placeholder="Search record id or name…"
            aria-label="Search conflicts by record id or name"
            [ngModel]="conflictSearch"
            (ngModelChange)="onConflictSearchChange($event)"
            name="conflictSearch"
            autocomplete="off"
          />
          <i class="bi bi-search" aria-hidden="true"></i>
        </div>
      </div>

      <p class="issue-count-live" aria-live="polite">
        <ng-container *ngIf="!loadingConflicts">
          {{ conflictFilteredCount }} conflict{{ conflictFilteredCount === 1 ? '' : 's' }} shown
        </ng-container>
        <ng-container *ngIf="loadingConflicts">Loading conflicts…</ng-container>
      </p>

      <div class="tbl-scroll" *ngIf="conflictRows.length">
        <table class="tbl">
          <thead>
            <tr><th>Type</th><th>Record</th><th>Field</th><th>Aivor value</th><th>Sage value</th><th>Kept</th><th>Detected</th></tr>
          </thead>
          <tbody>
            <ng-container *ngFor="let row of conflictRows; trackBy: trackIssue">
              <ng-container *ngIf="row.conflict as c">
                <tr *ngFor="let f of c.fields">
                  <td>
                    <ng-container *ngTemplateOutlet="conflictEntityPill; context: { $implicit: row }"></ng-container>
                  </td>
                  <td class="cell-primary">{{ row.recordLabel }}</td>
                  <td class="code">{{ f.fieldName }}</td>
                  <td><span class="val-chip" [class.winner]="c.winningSystem === 1">{{ f.aivorValue || '—' }}</span></td>
                  <td><span class="val-chip" [class.winner]="c.winningSystem === 2">{{ f.sageValue || '—' }}</span></td>
                  <td><span class="won-pill">{{ winningSystemName(c.winningSystem) }}</span></td>
                  <td class="mono">{{ row.detectedAt | date: 'dd MMM yyyy, HH:mm' }}</td>
                </tr>
                <!-- Without this, a conflict logged with no fields would vanish from the table. -->
                <tr *ngIf="!c.fields.length">
                  <td>
                    <ng-container *ngTemplateOutlet="conflictEntityPill; context: { $implicit: row }"></ng-container>
                  </td>
                  <td class="cell-primary">{{ row.recordLabel }}</td>
                  <td colspan="3" class="empty-cell">No field-level detail was recorded.</td>
                  <td><span class="won-pill">{{ winningSystemName(c.winningSystem) }}</span></td>
                  <td class="mono">{{ row.detectedAt | date: 'dd MMM yyyy, HH:mm' }}</td>
                </tr>
              </ng-container>
            </ng-container>
          </tbody>
        </table>
      </div>

      <ng-template #conflictEntityPill let-row>
        <span class="entity-pill" [style.background]="entityAccentSoftFor(row.entityType)" [style.color]="entityAccentFor(row.entityType)">
          <i class="bi" [ngClass]="entityIconFor(row.entityType)"></i>{{ entityLabelFor(row.entityType) }}
        </span>
      </ng-template>

      <div class="err-empty" *ngIf="!loadingConflicts && !conflictRows.length">
        <i class="bi bi-check2-circle"></i>
        <span *ngIf="conflictSearch.trim() || selectedConflictEntityType">No conflicts match your filters.</span>
        <span *ngIf="!conflictSearch.trim() && !selectedConflictEntityType">
          No conflicts have been detected — no Sage changes have been replaced by Aivor.
        </span>
      </div>

      <div class="err-pager" *ngIf="conflictTotalPages > 1">
        <button type="button" [disabled]="conflictPage <= 1 || loadingConflicts" aria-label="Previous page" (click)="goToConflictPage(conflictPage - 1)">
          <i class="bi bi-chevron-left"></i>
        </button>
        <span>Page {{ conflictPage }} of {{ conflictTotalPages }}</span>
        <button type="button" [disabled]="conflictPage >= conflictTotalPages || loadingConflicts" aria-label="Next page" (click)="goToConflictPage(conflictPage + 1)">
          <i class="bi bi-chevron-right"></i>
        </button>
      </div>

      <div class="readonly-note" *ngIf="conflictRows.length">
        <i class="bi bi-info-circle"></i>When a record was changed in both Aivor and Sage, Aivor's version is kept. Sage's changes are listed here so you can see them, but they aren't copied into Aivor.
      </div>
    </section>

    <!-- Declared inside the component's own template, so it reads expandedIssueKey and calls
         toggleIssueRow directly — only the rows are passed in. -->
    <ng-template #issueRowList let-rows>
      <ng-container *ngFor="let row of asIssueRows(rows); trackBy: trackIssue">
        <div
          class="err-row-grid"
          role="button"
          tabindex="0"
          [attr.aria-expanded]="expandedIssueKey === row.key"
          (click)="toggleIssueRow(row)"
          (keydown.enter)="toggleIssueRow(row)"
          (keydown.space)="$event.preventDefault(); toggleIssueRow(row)"
        >
          <span
            class="entity-pill"
            [style.background]="entityAccentSoftFor(row.entityType)"
            [style.color]="entityAccentFor(row.entityType)"
          >
            <i class="bi" [ngClass]="entityIconFor(row.entityType)"></i>{{ entityLabelFor(row.entityType) }}
          </span>

          <div class="issue-subject">
            <div class="cell-primary">
              {{ row.recordLabel }}
              <span class="result-pill success" *ngIf="row.error?.isResolved">Resolved</span>
            </div>
            <div class="cell-sub">{{ row.error?.errorMessage }}</div>
          </div>

          <span class="issue-reason" [class.issue-code-api]="row.reasonIsApiCode">{{ row.reason || '—' }}</span>

          <span class="mono">{{ row.detectedAt | date: 'dd MMM, HH:mm' }}</span>
          <span class="chev">
            {{ expandedIssueKey === row.key ? 'Hide' : 'Detail' }}
            <i class="bi" [class.bi-chevron-down]="expandedIssueKey !== row.key" [class.bi-chevron-up]="expandedIssueKey === row.key"></i>
          </span>
        </div>

        <!-- Error detail -->
        <div class="err-detail" *ngIf="expandedIssueKey === row.key && row.type === 'error' && row.error as err">
          <div>
            <div class="err-detail-label">Error message</div>
            <pre class="err-payload">{{ err.errorMessage }}</pre>
            <ng-container *ngIf="err.hasResponseBody">
              <div class="err-detail-label">Sage's reply</div>
              <p *ngIf="loadingErrorDetail" class="err-hint">Loading…</p>
              <pre class="err-payload" *ngIf="expandedErrorDetail?.sageResponseBody">{{ expandedErrorDetail?.sageResponseBody }}</pre>
            </ng-container>
          </div>
          <div>
            <div class="err-side-card">
              <div class="err-detail-label">Record</div>
              <div class="err-field-name">{{ err.recordLabel }}</div>
              <div class="err-hint">Source: {{ err.recordSource || '—' }}</div>
            </div>
            <div class="err-side-card">
              <div class="err-detail-label">Sync run</div>
              <div class="err-hint">{{ entityLabelFor(row.entityType) }} &middot; {{ err.syncStartedAt | date: 'short' }}</div>
            </div>
          </div>
        </div>
      </ng-container>
    </ng-template>

    <!-- Review queue -->
    <section class="tab-panel" *ngIf="activeTab === 'review'">
      <button class="back-link" (click)="backToDashboard()">
        <i class="bi bi-arrow-left"></i>Back to dashboard
      </button>
      <div class="rq-empty" *ngIf="!reviewTotalCount">
        <div class="rq-empty-tile"><i class="bi bi-check2-circle"></i></div>
        <div>
          <div class="rq-empty-title">Nothing is waiting for review</div>
          <div class="rq-empty-sub">Every record Sage has sent since onboarding has been applied.</div>
        </div>
      </div>

      <div class="rq-card" *ngIf="reviewTotalCount">
        <div class="rq-head">
          <div class="rq-head-tile"><i class="bi bi-inbox"></i></div>
          <div class="rq-head-text">
            <div class="rq-head-title">
              {{ reviewTotalCount }} record{{ reviewTotalCount === 1 ? '' : 's' }} awaiting a decision
            </div>
            <div class="rq-head-sub">
              {{ reviewEntityCount }} record type{{ reviewEntityCount === 1 ? '' : 's' }}
              <span *ngIf="oldestReviewAge"> · oldest waiting {{ oldestReviewAge }}</span>
            </div>
          </div>
          <button class="rq-btn rq-btn-primary" [disabled]="applyBusy || !syncEnabled" (click)="openApplyAllConfirm()">
            <i class="bi bi-check2-all"></i>{{ applyingAllReview ? 'Applying…' : 'Apply reviewed records' }}
          </button>
        </div>

        <div class="rq-row" *ngFor="let r of reviewEntitiesOrdered">
          <span class="rq-urgency" [style.background]="entityAccent(r.entityType)"></span>

          <div class="rq-identity">
            <div class="rq-tile" [style.background]="entityAccentSoft(r.entityType)" [style.color]="entityAccent(r.entityType)">
              <i class="bi" [ngClass]="entityIcon(r.entityType)"></i>
            </div>
            <div class="rq-identity-text">
              <span class="rq-name">{{ entityLabel(r.entityType) }}</span>
              <span class="rq-pill rq-pill-age" [class.rq-pill-stale]="isStale(r.sessionCreatedAt)" *ngIf="ageLabel(r.sessionCreatedAt)">
                <i class="bi bi-clock-history"></i>{{ ageLabel(r.sessionCreatedAt) }}
              </span>
              <span class="rq-pill rq-pill-dep" *ngIf="dependencyPill(r)">{{ dependencyPill(r) }}</span>
            </div>
          </div>

          <div class="rq-count">
            <span class="rq-count-num">{{ r.reviewCount }}</span>
            <span class="rq-count-label">to review</span>
          </div>

          <div class="rq-actions">
            <button class="rq-btn rq-btn-primary" [disabled]="!entitySlug(r.entityType)" (click)="openReview(r)">
              Review<i class="bi bi-arrow-right"></i>
            </button>
            <button class="rq-btn rq-btn-secondary" [disabled]="applyBusy || !syncEnabled" (click)="applyReview(r)">
              {{ applyingReviewSessionId === r.sessionId ? 'Applying…' : 'Apply' }}
            </button>
          </div>
        </div>

        <div class="rq-foot" *ngIf="emptyEntityLabels.length">
          <i class="bi bi-check2-circle"></i>
          <span>{{ emptyEntityLabels.join(', ') }} ha{{ emptyEntityLabels.length === 1 ? 's' : 've' }} nothing waiting.</span>
        </div>
      </div>
    </section>

    <!-- Stock corrections -->
    <section class="tab-panel table-card" *ngIf="activeTab === 'stock'">
      <div class="section-head">
        <button class="back-btn" title="Back to dashboard" aria-label="Back to dashboard" (click)="backToDashboard()">
          <i class="bi bi-arrow-left"></i>
        </button>
        <div class="icon-badge warn"><i class="bi bi-clipboard-data"></i></div>
        <div class="section-head-text">
          <h3>Stock corrections</h3>
          <p *ngIf="stockBadgeCount">
            {{ stockBadgeCount }} item(s) hold different stock quantities in Aivor and Sage. Nothing is sent until you choose a reason.
          </p>
          <p *ngIf="!stockBadgeCount">Aivor and Sage agree on every linked item's stock quantity.</p>
        </div>
      </div>
      <div class="tbl-scroll" *ngIf="stockAdjustments.length">
        <table class="tbl">
          <thead>
            <tr><th>Item</th><th>Aivor qty</th><th>Sage qty</th><th>Correction</th><th>Correct in</th><th>Status</th><th>Detected</th><th></th></tr>
          </thead>
          <tbody>
            <tr *ngFor="let a of stockAdjustments">
              <td>
                <div class="cell-primary">{{ a.itemName || ('Item #' + a.itemId) }}</div>
                <div class="cell-sub code" *ngIf="a.itemCode">{{ a.itemCode }}</div>
              </td>
              <td><span class="val-chip" [class.winner]="a.direction === 1">{{ a.aivorQuantity }}</span></td>
              <td><span class="val-chip" [class.winner]="a.direction === 2">{{ a.sageQuantity }}</span></td>
              <td class="mono">{{ a.delta > 0 ? '+' : '' }}{{ a.delta }}</td>
              <td><span class="won-pill">{{ stockCorrectionTargetName(a.direction) }}</span></td>
              <td>
                <span class="chip">{{ stockAdjustmentStatusName(a.status) }}</span>
                <div class="cell-sub" *ngIf="a.reasonDescription">{{ a.reasonDescription }}</div>
                <div class="cell-sub muted" *ngIf="a.lastError">{{ a.lastError }}</div>
              </td>
              <td class="muted">{{ a.detectedAt | date: 'short' }}</td>
              <td>
                <ng-container *ngIf="isStockAdjustmentOpen(a)">
                  <button class="btn btn-primary" [disabled]="!syncEnabled" (click)="openReleaseDialog(a)">Choose reason…</button>
                  <button class="btn btn-secondary" (click)="cancelStockAdjustment(a)">Dismiss</button>
                </ng-container>
              </td>
            </tr>
            <tr *ngIf="!stockAdjustments.length">
              <td colspan="8" class="empty-cell">No stock corrections outstanding.</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="readonly-note" *ngIf="stockAdjustments.length">
        <i class="bi bi-info-circle"></i>One system is in charge of your stock count, and the other is corrected to match it. Both counts are checked again when you apply, so the correction always uses the latest numbers.
      </div>
    </section>

    <!-- GL reporting groups -->
    <section class="tab-panel table-card" *ngIf="activeTab === 'groups'">
      <div class="section-head">
        <div class="icon-badge teal"><i class="bi bi-tags-fill"></i></div>
        <div class="section-head-text">
          <h3>Sales and purchase groups</h3>
          <p>{{ activeGroupCount }} active group(s) from Sage &middot; {{ inactiveGroupCount }} inactive</p>
        </div>
        <div class="grp-head-actions">
          <div class="grp-synced">
            <span class="reason-label">Last refreshed</span>
            <span class="mono">{{ groupsLastSyncedAt ? (groupsLastSyncedAt | date: 'short') : 'Never' }}</span>
          </div>
          <button class="btn btn-secondary" (click)="promptSetDefaultGroup()">
            <i class="bi bi-star"></i>
            Choose default sales group
          </button>
          <button class="btn btn-primary" [disabled]="refreshingGroups || !syncEnabled" (click)="refreshReportGroups()">
            <i class="bi bi-arrow-repeat" [class.spin]="refreshingGroups"></i>
            {{ refreshingGroups ? 'Refreshing…' : 'Refresh from Sage' }}
          </button>
        </div>
      </div>

      <div class="issue-filter-row">
        <span class="reason-label" id="groupTypeLabel">Type</span>
        <div class="reason-chips" role="group" aria-labelledby="groupTypeLabel">
          <button type="button" class="err-chip" [class.active]="groupTypeFilter === 0" (click)="selectGroupType(0)">
            All <span class="err-chip-count">{{ groupTypeCount(0) }}</span>
          </button>
          <button
            type="button"
            class="err-chip"
            *ngFor="let t of reportGroupTypes"
            [class.active]="groupTypeFilter === t.id"
            [attr.aria-pressed]="groupTypeFilter === t.id"
            (click)="selectGroupType(t.id)"
          >
            {{ t.label }} <span class="err-chip-count">{{ groupTypeCount(t.id) }}</span>
          </button>
        </div>

        <div class="grp-filter-right">
          <button
            type="button"
            class="grp-toggle"
            [class.on]="showInactiveGroups"
            [attr.aria-pressed]="showInactiveGroups"
            (click)="toggleInactiveGroups()"
          >
            <i class="bi" [class.bi-square]="!showInactiveGroups" [class.bi-check-square-fill]="showInactiveGroups"></i>
            Show inactive
          </button>
          <div class="search-input">
            <i class="bi bi-search"></i>
            <input type="text" placeholder="Search group or Sage ID" aria-label="Search sales and purchase groups" [(ngModel)]="groupSearch" name="groupSearch" autocomplete="off" />
          </div>
        </div>
      </div>

      <div class="tbl-scroll">
        <table class="tbl">
          <thead>
            <tr><th>Group</th><th>Applies to</th><th>Sage ID</th><th>Status</th></tr>
          </thead>
          <tbody>
            <tr *ngFor="let g of filteredGroups; trackBy: trackGroup" [class.grp-row-inactive]="!g.isActive">
              <td>
                <div class="grp-name-line">
                  <span class="cell-primary" [class.muted]="!g.isActive">{{ g.description }}</span>
                  <span class="grp-default-pill" *ngIf="isDefaultSalesGroup(g)" title="Used for every item that doesn't have its own sales group">
                    <i class="bi bi-star-fill"></i>Default for items
                  </span>
                </div>
                <div class="cell-sub">{{ g.isActive ? 'Group #' + g.id : 'No longer returned by Sage' }}</div>
              </td>
              <td>
                <span
                  class="entity-pill"
                  [style.background]="reportGroupType(g.itemReportingGroupTypeId).bg"
                  [style.color]="reportGroupType(g.itemReportingGroupTypeId).fg"
                >
                  <i class="bi" [ngClass]="reportGroupType(g.itemReportingGroupTypeId).icon"></i>
                  {{ reportGroupType(g.itemReportingGroupTypeId).label }}
                </span>
              </td>
              <td class="mono">{{ g.sageItemReportingGroupId ?? '—' }}</td>
              <td>
                <span class="result-pill" [class.success]="g.isActive" [class.grp-inactive-pill]="!g.isActive">
                  {{ g.isActive ? 'Active' : 'Inactive' }}
                </span>
              </td>
            </tr>
            <tr *ngIf="!loadingGroups && !filteredGroups.length">
              <td colspan="4" class="empty-cell">No groups match your filters.</td>
            </tr>
            <tr *ngIf="loadingGroups">
              <td colspan="4" class="empty-cell">Loading groups…</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="readonly-note">
        <i class="bi bi-info-circle"></i>
        These groups are set up in Sage, and Aivor shows a copy here. Refresh from Sage to pick up new ones. A group
        removed in Sage shows here as inactive rather than being deleted, so past sales and purchases keep their group.
      </div>
    </section>

  </ng-container>

  <!-- Disconnect confirm -->
  <div class="modal-backdrop" *ngIf="showDisconnectConfirm" (click)="cancelDisconnect()">
    <div class="modal-card" (click)="$event.stopPropagation()">
      <h3>Disconnect Sage?</h3>
      <p>
        Aivor will stop syncing with Sage until you reconnect. Your company link, matches and review
        history are kept — reconnecting brings you straight back to this console.
      </p>
      <div class="modal-actions">
        <button class="btn btn-secondary" (click)="cancelDisconnect()">Cancel</button>
        <button class="btn btn-danger" (click)="confirmDisconnect()">Disconnect</button>
      </div>
    </div>
  </div>

  <!-- Set default sales reporting group -->
  <div class="modal-backdrop" *ngIf="showSetDefaultGroupPrompt" (click)="cancelSetDefaultGroup()">
    <div class="modal-card" (click)="$event.stopPropagation()">
      <h3>Choose a sales group for your items</h3>
      <p>
        In Sage, every item belongs to a sales group (Sage calls it a "sales reporting group"). It decides
        which income account in your Sage books that item's sales are recorded under.
      </p>
      <p>
        Your Aivor items don't have one yet, so Aivor can't send them to Sage until you choose.
      </p>
      <p>
        Pick the group most of your items belong under; your bookkeeper or accountant will know if you're
        not sure. Aivor uses it for every item that doesn't have its own group, and you can change it here later.
      </p>
      <select class="form-select" [(ngModel)]="selectedDefaultGroupId" name="defaultGroupId" aria-label="Default sales group">
        <option [ngValue]="null" disabled>Choose a sales group…</option>
        <option *ngFor="let g of salesReportGroupOptions" [ngValue]="g.id">{{ g.description }}</option>
      </select>
      <div class="modal-actions">
        <button class="btn btn-secondary" (click)="cancelSetDefaultGroup()">Not now</button>
        <button
          class="btn btn-primary"
          [disabled]="!selectedDefaultGroupId || savingDefaultGroup"
          (click)="confirmSetDefaultGroup()"
        >
          {{ savingDefaultGroup ? 'Saving…' : 'Use this group' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Apply-all confirm -->
  <div class="modal-backdrop" *ngIf="showApplyAllConfirm" (click)="cancelApplyAll()">
    <div class="modal-card" (click)="$event.stopPropagation()">
      <h3>Apply reviewed records?</h3>
      <p>
        Decisions you have made on {{ reviewEntityNames }} are locked in and a sync runs for each.
        Records you haven't decided on stay in the review, and nothing is sent to Sage for them
        until you decide.
      </p>
      <div class="modal-actions">
        <button class="btn btn-secondary" (click)="cancelApplyAll()">Cancel</button>
        <button class="btn btn-primary" [disabled]="applyBusy || !syncEnabled" (click)="applyAllReviewed()">
          Apply reviewed records
        </button>
      </div>
    </div>
  </div>

  <!-- Finished goods switched after they're in Sage -->
  <div class="modal-backdrop" *ngIf="pendingTargetSwitches as switches" (click)="cancelTargetSwitches()">
    <div class="modal-card" role="dialog" aria-modal="true" aria-labelledby="target-switch-title" (click)="$event.stopPropagation()">
      <h3 id="target-switch-title">Change how {{ switches.length === 1 ? 'this is' : 'these are' }} sent to Sage?</h3>
      <p>{{ switches.length === 1 ? 'This finished good is' : 'These finished goods are' }} already in Sage:</p>
      <ul class="modal-list">
        <li *ngFor="let t of switches">{{ t.itemCode }} {{ t.itemName }}: {{ t.originalTarget }} → {{ t.target }}</li>
      </ul>
      <p>
        A bundle and an item are different records in Sage. The next sync creates the new one, or reuses one sent
        before, and makes the old one inactive in Sage. Any stock the old one holds stays on it in Sage.
      </p>
      <div class="modal-actions">
        <button type="button" class="btn btn-secondary" (click)="cancelTargetSwitches()">Cancel</button>
        <button type="button" class="btn btn-primary" (click)="confirmTargetSwitches()">Save changes</button>
      </div>
    </div>
  </div>

  <div class="modal-backdrop" *ngIf="releasingAdjustment" (click)="cancelRelease()">
    <div class="modal-card" (click)="$event.stopPropagation()">
      <h3>Why is this stock being corrected?</h3>
      <p>
        {{ releasingAdjustment.itemName || ('Item #' + releasingAdjustment.itemId) }} will be adjusted by
        <strong>{{ releasingAdjustment.delta > 0 ? '+' : '' }}{{ releasingAdjustment.delta }}</strong>
        in {{ stockCorrectionTargetName(releasingAdjustment.direction) }}.
      </p>
      <select class="conflict-entity-select" [(ngModel)]="selectedReasonId" name="stockAdjustmentReason">
        <option [ngValue]="null">Select a reason…</option>
        <option *ngFor="let r of stockReasons" [ngValue]="r.id">{{ r.description }}</option>
      </select>
      <div class="modal-actions">
        <button class="btn btn-secondary" (click)="cancelRelease()">Cancel</button>
        <button class="btn btn-primary" [disabled]="!syncEnabled || !selectedReasonId || releasing" (click)="confirmRelease()">
          {{ releasing ? 'Applying…' : 'Apply correction' }}
        </button>
      </div>
    </div>
  </div>

  <div class="side-backdrop" *ngIf="changingLink" (click)="closeLinkChange()">
    <aside class="side-panel" role="dialog" aria-modal="true" aria-labelledby="fixMatchPanelTitle" (click)="$event.stopPropagation()">
      <div class="side-panel-head">
        <div>
          <span class="side-panel-tag" id="fixMatchPanelTitle">Change {{ fixNoun }} link</span>
          <h3 class="side-panel-title">{{ changingLink.originalSageName }}</h3>
          <p class="side-panel-sub">{{ linkRecordLine(changingLink) }}</p>
        </div>
        <button type="button" class="side-panel-close" aria-label="Close" [disabled]="savingLink" (click)="closeLinkChange()">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
      <div class="side-panel-body">
        <app-fix-match-change
          [type]="fixType"
          [changing]="changingLink"
          [showRecord]="false"
          [rows]="linkRows"
          [units]="linkUnits"
          [saving]="savingLink"
          [error]="linkError"
          (move)="saveLinkMove($event)"
          (addAsNew)="saveLinkAdd($event)"
          (cancel)="closeLinkChange()"
        ></app-fix-match-change>
      </div>
    </aside>
  </div>

  <div class="side-backdrop" *ngIf="errorFix" (click)="closeErrorFix()">
    <aside class="side-panel" role="dialog" aria-modal="true" aria-labelledby="errorFixPanelTitle" (click)="$event.stopPropagation()">
      <div class="side-panel-head">
        <div>
          <span class="side-panel-tag">{{ errorFix.entityPill || 'Errors you can fix' }}</span>
          <h3 class="side-panel-title" id="errorFixPanelTitle">{{ errorFix.label }}</h3>
        </div>
        <button type="button" class="side-panel-close" aria-label="Close" (click)="closeErrorFix()">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
      <div class="side-panel-body">
        <app-error-fix-panel
          [group]="errorFix"
          [store]="fixStore"
          [unitFix]="unitFix"
          [syncEnabled]="syncEnabled"
          [syncBusy]="syncBusy"
          [syncing]="fixSyncing"
          [synced]="fixSynced"
          [groupsLastRead]="groupsLastSyncedAt"
          idPrefix="web-"
          (action)="runErrorFixAction(errorFix)"
          (sync)="syncForFix($event)"
          (openMatching)="openMatchingForFix($event)"
          (close)="closeErrorFix()"
          (unitGroupChange)="chooseFixGroupUnit($event)"
          (unitItemChange)="chooseFixUnit($event)"
          (unitApply)="applyUnitFix()"
        ></app-error-fix-panel>
      </div>
    </aside>
  </div>
</div>
</div>

<app-mobile-sage-console-view
  class="d-md-none"
  *ngIf="!loading"
  [activeTab]="activeTab"
  [status]="status"
  [lastSyncedAt]="lastSyncedAt"
  [loadError]="loadError"
  [syncEnabled]="syncEnabled"
  [syncState]="syncState"
  [syncProgress]="syncProgress"
  [showSyncStepper]="showSyncStepper"
  [syncAllShowsSyncing]="syncAllShowsSyncing"
  [connSubtitle]="connSubtitle"
  [entityStatuses]="entityStatuses"
  [cardSyncState]="cardSyncStateFn"
  [actionableCountFor]="actionableCountFn"
  [trackEntity]="trackEntity"
  [reviewGroups]="reviewGroups"
  [reviewTotalCount]="reviewTotalCount"
  [loadingReviewGroups]="loadingReviewGroups"
  [errorGroups]="errorGroups"
  [retryingErrorCount]="retryingErrorCount"
  [loadingErrorGroups]="loadingErrorGroups"
  [dashStockRows]="dashStockRows"
  [dashStockTotal]="dashStockTotal"
  [stockReasons]="stockReasons"
  [loadingDashStock]="loadingDashStock"
  [conflictBadgeCount]="conflictBadgeCount"
  [disconnecting]="disconnecting"
  (tabSelected)="selectTab($event)"
  (syncAll)="syncAllNow()"
  (syncEntity)="syncEntity($event)"
  (reviewGroupOpened)="openReviewGroup($event)"
  (errorGroupFixed)="fixErrorGroup($event)"
  (dashStockChanged)="onDashStockChanged()"
  (syncDisabled)="syncEnabled = false"
  (disconnectRequested)="disconnectSage()"
  [recentLogs]="recentLogs"
  [loadingRuns]="loadingRuns"
  [runsPage]="runsPage"
  [runsTotalPages]="runsTotalPages"
  [errorRows]="errorRows"
  [errorSearch]="errorSearch"
  [errorPage]="errorPage"
  [errorTotalPages]="errorTotalPages"
  [errorFilteredCount]="errorFilteredCount"
  [errorTotalCount]="errorTotalCount"
  [loadingErrors]="loadingErrors"
  [errorCategoryCounts]="errorCategoryCounts"
  [selectedErrorCategories]="selectedErrorCategories"
  [showResolvedErrors]="showResolvedErrors"
  [reasonChipsVisible]="reasonChipsVisible"
  [expandedIssueKey]="expandedIssueKey"
  [expandedErrorDetail]="expandedErrorDetail"
  [loadingErrorDetail]="loadingErrorDetail"
  [trackIssue]="trackIssue"
  [conflictRows]="conflictRows"
  [conflictSearch]="conflictSearch"
  [conflictPage]="conflictPage"
  [conflictTotalPages]="conflictTotalPages"
  [conflictFilteredCount]="conflictFilteredCount"
  [loadingConflicts]="loadingConflicts"
  [conflictEntityOptions]="conflictEntityOptions"
  [selectedConflictEntityType]="selectedConflictEntityType"
  [reviewEntitiesOrdered]="reviewEntitiesOrdered"
  [reviewEntityCount]="reviewEntityCount"
  [oldestReviewAge]="oldestReviewAge"
  [reviewEntityNames]="reviewEntityNames"
  [emptyEntityLabels]="emptyEntityLabels"
  [applyingReviewSessionId]="applyingReviewSessionId"
  [applyBusy]="applyBusy"
  [applyingAllReview]="applyingAllReview"
  [dependencyPill]="dependencyPillFn"
  [stockAdjustments]="stockAdjustments"
  [stockBadgeCount]="stockBadgeCount"
  [isStockAdjustmentOpen]="isStockAdjustmentOpenFn"
  [releasingAdjustment]="releasingAdjustment"
  [selectedReasonId]="selectedReasonId"
  [releasing]="releasing"
  [targetCount]="targets.length"
  [filteredTargets]="filteredTargets"
  [targetsSearch]="targetsSearch"
  [dirtyTargetCount]="dirtyTargetCount"
  [unsetTargetCount]="unsetTargetCount"
  [savingTargets]="savingTargets"
  [pendingTargetSwitches]="pendingTargetSwitches"
  [isTargetBlocked]="isTargetBlockedFn"
  [targetBlockedReason]="targetBlockedReasonFn"
  [openProvenanceItemId]="openProvenanceItemId"
  [provenanceFor]="provenanceForFn"
  [provenanceStateFor]="provenanceStateForFn"
  [fixMatches]="fixMatchesView"
  [errorFix]="errorFix"
  [fixStore]="fixStore"
  [fixSyncBusy]="syncBusy"
  [fixSyncing]="fixSyncing"
  [fixSynced]="fixSynced"
  [unitFix]="unitFix"
  [filteredGroups]="filteredGroups"
  [groupTypeFilter]="groupTypeFilter"
  [groupSearch]="groupSearch"
  [showInactiveGroups]="showInactiveGroups"
  [activeGroupCount]="activeGroupCount"
  [inactiveGroupCount]="inactiveGroupCount"
  [groupTypeCount]="groupTypeCountFn"
  [isDefaultSalesGroup]="isDefaultSalesGroupFn"
  [loadingGroups]="loadingGroups"
  [refreshingGroups]="refreshingGroups"
  [groupsLastSyncedAt]="groupsLastSyncedAt"
  [showDisconnectConfirm]="showDisconnectConfirm"
  [showSetDefaultGroupPrompt]="showSetDefaultGroupPrompt"
  [salesReportGroupOptions]="salesReportGroupOptions"
  [selectedDefaultGroupId]="selectedDefaultGroupId"
  [savingDefaultGroup]="savingDefaultGroup"
  [showApplyAllConfirm]="showApplyAllConfirm"
  [tourActive]="tourOpen"
  [helpPulse]="helpPulse"
  (helpRequested)="openTour()"
  (backToDashboard)="backToDashboard()"
  (runsPageChange)="goToRunsPage($event)"
  (errorSearchChange)="onErrorSearchChange($event)"
  (errorCategoryToggled)="toggleErrorCategory($event)"
  (errorPageChange)="goToErrorPage($event)"
  (resolvedErrorsToggled)="toggleResolvedErrors()"
  (issueRowToggled)="toggleIssueRow($event)"
  (conflictSearchChange)="onConflictSearchChange($event)"
  (conflictEntityTypeChange)="selectedConflictEntityType = $event; onConflictEntityTypeChange()"
  (conflictPageChange)="goToConflictPage($event)"
  (reviewOpened)="openReview($event)"
  (reviewApplied)="applyReview($event)"
  (applyAllRequested)="openApplyAllConfirm()"
  (releaseRequested)="openReleaseDialog($event)"
  (stockAdjustmentCancelled)="cancelStockAdjustment($event)"
  (selectedReasonIdChange)="selectedReasonId = $event"
  (targetsSearchChange)="targetsSearch = $event"
  (targetToggled)="toggleTarget($event)"
  (provenanceToggled)="toggleProvenance($event)"
  (targetsSaved)="saveTargets()"
  (targetSwitchConfirmed)="confirmTargetSwitches()"
  (targetSwitchCancelled)="cancelTargetSwitches()"
  (fixSectionChange)="setFixSection($event)"
  (fixSearchChange)="linksSearch = $event"
  (fixChipChange)="linksChip = $event"
  (fixChangeOpened)="openLinkChange($event)"
  (fixChangeClosed)="closeLinkChange()"
  (fixMoveSaved)="saveLinkMove($event)"
  (fixAddSaved)="saveLinkAdd($event)"
  (errorFixAction)="runErrorFixAction($event)"
  (errorFixSync)="syncForFix($event)"
  (errorFixOpenMatching)="openMatchingForFix($event)"
  (errorFixClosed)="closeErrorFix()"
  (unitFixGroupUnitChange)="chooseFixGroupUnit($event)"
  (unitFixUnitChange)="chooseFixUnit($event)"
  (unitFixApplied)="applyUnitFix()"
  (groupTypeSelected)="selectGroupType($event)"
  (groupSearchChange)="groupSearch = $event"
  (inactiveGroupsToggled)="toggleInactiveGroups()"
  (groupsRefreshed)="refreshReportGroups()"
  (setDefaultGroupRequested)="promptSetDefaultGroup()"
  (disconnectConfirmed)="confirmDisconnect()"
  (disconnectCancelled)="cancelDisconnect()"
  (selectedDefaultGroupIdChange)="selectedDefaultGroupId = $event"
  (defaultGroupConfirmed)="confirmSetDefaultGroup()"
  (defaultGroupCancelled)="cancelSetDefaultGroup()"
  (applyAllConfirmed)="applyAllReviewed()"
  (applyAllCancelled)="cancelApplyAll()"
  (releaseConfirmed)="confirmRelease()"
  (releaseCancelled)="cancelRelease()"
></app-mobile-sage-console-view>

<!-- Outside both layouts: it covers whichever one is showing, along with the app's own menus. -->
<app-sage-tour
  *ngIf="tourOpen"
  [steps]="tourSteps"
  [offerHint]="tourOfferHint"
  (outcome)="onTourOutcome($event)"
  (closed)="onTourClosed()"
></app-sage-tour>
